import React from 'react';
import PropTypes from 'prop-types';

import { useI18n } from '@hydra/common/customHooks';
import { Translate } from '@hydra/common/components';
import { Criterium, CriteriumSkeleton, CriteriumIconAndText } from '@hydra/common/criterium';
import { useReviewContext } from '@hydra/common/context';

export const SslChecksOrder = [
  'httpRedirect',
  'sts',
  'expiration',
  'provider'
];

export const getSslSecuredConclusion = data => {
  if (!data.hasCertificate) {
    return <Translate content='ssl_secured_ko' />;
  }

  if (data.rightCa) {
    return <Translate content='ssl_secured_ok' />;
  }

  return data.commonName
    ? <Translate content='ssl_secured_avg' with={{ commonName: data.commonName }} />
    : <Translate content='ssl_secured_avg_missing_common_name' />;
};

export const getOkStatus = (data, check) => {
  switch (check) {
    case 'httpRedirect':
      return data.httpsRedirect;
    case 'sts':
      return data.stsHeader;
    case 'expiration':
      return !data.certificateExpired;
    case 'provider':
      return (!!data.certificateIssuer && !data.selfSigned);
    default:
      return false; // Shouldn't end up here
  }
};

export const getSubstitutions = (data, check, moment) => {
  switch (check) {
    case 'expiration':
      return {
        expirationDate: moment(data.certificateExpirationDate).fromNow()
      };
    case 'provider':
      return (data.selfSigned)
        ? { certificateIssuer: 'self-signed' }
        : { certificateIssuer: data.certificateIssuer };
    default:
      return undefined;
  }
};

export const calculateDataObjects = (data, moment) => {
  return SslChecksOrder.map(check => {
    const ok = getOkStatus(data, check);
    const textKey = ok ? `ssl_secured_${check}_ok` : `ssl_secured_${check}_ko`;
    const possibleSubstitutions = getSubstitutions(data, check, moment);
    const text = <Translate content={textKey} with={possibleSubstitutions} />;
    return { check, ok, text };
  });
};

export const SslSecured = ({ criterium }) => {
  const { moment } = useI18n();
  const { data, url } = criterium;
  const { variant } = useReviewContext();

  const sslSecuredConclusion = getSslSecuredConclusion(data);
  const calculatedData = calculateDataObjects(data, moment);

  const content = (
    <>
      <p>
        {variant === 'nocrit' ? <CriteriumSkeleton width='50%' /> : sslSecuredConclusion}
      </p>
      {
        data.hasCertificate
          ? calculatedData.map(check =>
            <CriteriumIconAndText key={check.check} success={check.ok} text={check.text} />)
          : null
      }
    </>
  );

  return (
    <Criterium
      criterium={criterium}
      variables={{ domain: url }}
    >
      {content}
    </Criterium>
  );
};

SslSecured.propTypes = {
  criterium: PropTypes.shape({
    data: PropTypes.shape({
      numberofjavascriptfiles: PropTypes.number,
      pagecaching: PropTypes.bool,
      numberofstylesheets: PropTypes.number,
      nestedtable: PropTypes.bool,
      inlinestyle: PropTypes.bool,
      gzip: PropTypes.bool
    })
  })
};

export default React.memo(SslSecured);
